import React, { FC } from 'react';
import { Avatar, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { DataTable, DataTableColumn, SocialIcon } from 'components';
import { AnalyticsProfile, GetOiCtaPeopleQuery } from 'api';

import { getEngagementType, getRoles, getTitles } from 'features/insights/helpers';
import { getAvatarSrc } from 'helpers';

interface Props {
  results: GetOiCtaPeopleQuery['getPeople']['results'];
}

const LandingTable: FC<Props> = ({ results }) => {
  const { t } = useTranslation();

  const columns: DataTableColumn<AnalyticsProfile>[] = [
    {
      width: 64,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: profile => (
          <Link
            display="block"
            underline="none"
            target="_blank"
            title={profile.name}
            href={profile.network_profile_url}
          >
            <Avatar
              src={getAvatarSrc(profile.network_profile_image, 56)}
              style={{ height: 56, width: 56 }}
            />
          </Link>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: profile => (
          <Link
            noWrap
            display="block"
            underline="hover"
            variant="subtitle2"
            target="_blank"
            title={profile.name}
            href={profile.network_profile_url}
          >
            {profile.name}
          </Link>
        ),
      },
    },
    {
      width: 64,
      headerCell: {
        render: t('common:company'),
      },
      bodyCell: {
        render: profile => (
          <Avatar
            variant="square"
            src={profile.company.company_logo}
            style={{ height: 56, width: 56, borderRadius: 4 }}
          />
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: profile => (
          <Typography title={profile.company.job_company_name} noWrap variant="subtitle1">
            {profile.company.job_company_name}
          </Typography>
        ),
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:title'),
      },
      bodyCell: {
        render: profile => {
          const titles = getTitles(profile);
          return (
            <Typography noWrap variant="body2" title={titles}>
              {titles}
            </Typography>
          );
        },
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:role'),
      },
      bodyCell: {
        render: profile => {
          const roles = getRoles(profile);
          return (
            <Typography noWrap variant="body2" title={roles}>
              {roles}
            </Typography>
          );
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:network'),
      },
      bodyCell: {
        render: profile => {
          try {
            const { last_engagement_detail } = profile.engagement_summary;
            const { network, share_url } = last_engagement_detail;
            return (
              <Link display="block" underline="none" target="_blank" href={share_url}>
                <SocialIcon provider={network} />
              </Link>
            );
          } catch (error) {
            return null;
          }
        },
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:action'),
      },
      bodyCell: {
        render: profile => {
          try {
            const { last_engagement_detail } = profile.engagement_summary;
            const { engagement_type, share_url } = last_engagement_detail;
            const engagementText = getEngagementType(engagement_type);

            return (
              <Link
                display="block"
                underline="hover"
                target="_blank"
                href={share_url}
                variant="body2"
                title={engagementText}
                noWrap
              >
                {engagementText}
              </Link>
            );
          } catch (error) {
            return null;
          }
        },
      },
    },
    {
      width: 220,
      headerCell: {
        render: t('insights:last_engagement'),
      },
      bodyCell: {
        render: profile => {
          const mom = moment.unix(profile.engagement_summary.last_engagement);
          return <Typography>{mom.format('LLL')}</Typography>;
        },
      },
    },
  ];

  return (
    <DataTable
      // @ts-ignore
      data={results}
      columns={columns}
      actions={() => undefined}
      rowSelection={null}
      pagination={{ sentinelRef: null, isExhausted: true, isInitialLoad: false }}
    />
  );
};

export default LandingTable;
